import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Container, Divider, FormControl, InputLabel, MenuItem, Paper, Select, Stack, TextField, Typography } from '@mui/material';
import { AssignmentReturnOutlined, Search, TaskAlt } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../../api/api';
import { HandoverAct, ReturnCalculation } from '../../../client/ClientReturn';
import ReturnPointSelect from './ReturnPointSelect';

const labels = {
  requested: ['Ожидает сдачи', 'warning'],
  received: ['Принято на проверку', 'info'],
  refund_pending: ['Возврат денег обрабатывается', 'info'],
  completed: ['Завершён', 'success'],
};

const normalizeCode = value => value.toUpperCase().replace(/[^A-ZА-ЯЁ0-9]/g, '').slice(0, 16);

export default function ReturnsPage() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const [queue, setQueue] = useState([]);
  const [comment, setComment] = useState('');
  const [inspectionResult, setInspectionResult] = useState('ok');
  const [problemAmount, setProblemAmount] = useState('0');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rentalPointId, setRentalPointId] = useState('');

  const loadQueue = async () => {
    try {
      const response = await api.get('/client-returns/staff', { skipLoading: true });
      setQueue((response.data || []).filter(item => ['requested', 'received'].includes(item.status)));
    } catch { /* lookup remains usable if the queue is temporarily unavailable */ }
  };

  useEffect(() => { loadQueue(); }, []);

  const find = async event => {
    event?.preventDefault();
    if (!code) { setError('Введите код возврата.'); return; }
    setLoading(true); setError(''); setResult(null); setRentalPointId('');
    try {
      const response = await api.get(`/client-returns/staff/code/${encodeURIComponent(code)}`, { skipLoading: true });
      setResult(response.data);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Не удалось найти возврат.');
    } finally { setLoading(false); }
  };

  const receive = async () => {
    if (result.return_point_required && !rentalPointId) { setError('Выберите фактический ПВЗ возврата.'); return; }
    setLoading(true); setError('');
    try {
      const response = await api.post(`/client-returns/staff/${result.id}/receive`, {
        received_at: new Date().toISOString(), ...(rentalPointId ? { rental_point_id: Number(rentalPointId) } : {}),
      }, { skipLoading: true });
      setResult(response.data); await loadQueue();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Не удалось принять вещь.');
    } finally { setLoading(false); }
  };

  const complete = async () => {
    setLoading(true); setError('');
    try {
      const response = await api.post(`/client-returns/staff/${result.id}/complete`, {
        comment, inspection_result: inspectionResult, problem_amount: Number(problemAmount) || 0,
      }, { skipLoading: true });
      setResult(response.data); await loadQueue();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Не удалось завершить возврат.');
    } finally { setLoading(false); }
  };

  const status = result ? (labels[result.status] || [result.status, 'default']) : null;
  return <Container maxWidth="md" sx={{ py: 3 }}>
    <Typography variant="h4" fontWeight={800} sx={{ mb: .5 }}>Возвраты</Typography>
    <Typography color="text.secondary" sx={{ mb: 3 }}>Введите код с экрана клиента. Поле уже подходит для USB-сканера штрихкодов.</Typography>
    <Paper component="form" onSubmit={find} sx={{ p: 2, display: 'flex', gap: 1, alignItems: 'flex-start', mb: 3 }}>
      <TextField autoFocus fullWidth label="Код возврата" value={code} onChange={event => setCode(normalizeCode(event.target.value))} inputProps={{ maxLength: 16, inputMode: 'numeric' }} helperText="Можно ввести вручную или отсканировать" />
      <Button type="submit" variant="contained" size="large" startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Search />} disabled={loading} sx={{ minHeight: 56 }}>Найти</Button>
    </Paper>
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    {result && <Card sx={{ mb: 3 }}><CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
        <Box><Typography variant="h6" fontWeight={750}>{result.asset_name}</Typography><Typography color="text.secondary">Договор №{result.deal_number} · {result.client_name}{result.client_phone ? ` · ${result.client_phone}` : ''}</Typography></Box>
        <Chip label={status[0]} color={status[1]} />
      </Stack>
      <Divider sx={{ my: 2 }} />
      <ReturnCalculation value={result.settlement} />
      {result.return_act && <HandoverAct act={result.return_act} />}
      {result.received_at && <Alert severity="info" sx={{ mb: 2 }}>Фактическая сдача зафиксирована: {new Date(result.received_at).toLocaleString('ru-RU')}. Начисление остановлено.</Alert>}
      {result.status === 'requested' && <ReturnPointSelect value={rentalPointId} onChange={setRentalPointId} required={!!result.return_point_required} disabled={loading} />}
      {result.status === 'received' && <Stack spacing={2} sx={{ mb: 2 }}>
        <FormControl fullWidth><InputLabel id="inspection-result-label">Состояние товара</InputLabel><Select labelId="inspection-result-label" label="Состояние товара" value={inspectionResult} onChange={event => setInspectionResult(event.target.value)}>
          <MenuItem value="ok">Исправен и комплектен</MenuItem><MenuItem value="damaged">Повреждён</MenuItem><MenuItem value="incomplete">Неполная комплектация</MenuItem><MenuItem value="lost">Товар утрачен</MenuItem>
        </Select></FormControl>
        {inspectionResult !== 'ok' && <TextField type="number" label="Ущерб / задолженность, ₽" value={problemAmount} onChange={event => setProblemAmount(event.target.value)} inputProps={{ min: 0, max: 10000000 }} />}
        <TextField fullWidth multiline rows={2} required={inspectionResult !== 'ok'} label="Результат проверки" value={comment} onChange={event => setComment(event.target.value)} inputProps={{ maxLength: 1000 }} />
      </Stack>}
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
        <Button variant="outlined" onClick={() => navigate(`/rental-deals/edit/${result.deal_id}`)}>Открыть сделку</Button>
        {result.status === 'requested' && <Button variant="contained" startIcon={<AssignmentReturnOutlined />} disabled={loading || (result.return_point_required && !rentalPointId)} onClick={receive}>Принять вещь</Button>}
        {result.status === 'received' && <Button variant="contained" color={inspectionResult === 'ok' ? 'success' : 'warning'} startIcon={<TaskAlt />} disabled={loading || (inspectionResult !== 'ok' && !comment.trim())} onClick={complete}>{inspectionResult === 'ok' ? 'Проверка завершена — освободить товар' : 'Зафиксировать проблему'}</Button>}
      </Stack>
    </CardContent></Card>}
    <Typography variant="h6" fontWeight={750} sx={{ mb: 1 }}>Ожидающие возвраты</Typography>
    {queue.length === 0 ? <Typography color="text.secondary">Нет заявок, ожидающих приёма.</Typography> : <Stack spacing={1}>{queue.map(item => <Paper key={item.id} variant="outlined" sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}><Box><Typography fontWeight={650}>{item.asset_name} · №{item.deal_number}</Typography><Typography variant="body2" color="text.secondary">{item.client_name} · код {item.return_code}</Typography></Box><Button disabled={loading} onClick={() => { setCode(item.return_code); setResult(item); setRentalPointId(''); setError(''); }}>{item.status === 'received' ? 'Проверить' : 'Принять'}</Button></Paper>)}</Stack>}
  </Container>;
}
